import { createAsyncThunk } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { chaosApi } from 'src/api';

export const fetchChaosActions = createAsyncThunk('chaos/fetch-actions', async (_, thunkApi) => {
  try {
    const response = await chaosApi.fetchChaosActions();
    return response.data;
  } catch (error) {
    const message = error?.response?.data?.message || 'Failed to fetch Chaos actions';
    toast(message, { type: 'error' });
    return thunkApi.rejectWithValue(message);
  }
});

export const initChaosDefaults = createAsyncThunk('chaos/init-defaults', async (_, thunkApi) => {
  try {
    const response = await chaosApi.initChaosDefaults();
    toast(response?.data?.message || 'Chaos defaults initialized', { type: 'success' });
    thunkApi.dispatch(fetchChaosActions());
    return response.data;
  } catch (error) {
    const message = error?.response?.data?.message || 'Failed to init Chaos defaults';
    toast(message, { type: 'error' });
    return thunkApi.rejectWithValue(message);
  }
});

export const syncChaosNewActions = createAsyncThunk(
  'chaos/sync-new-actions',
  async ({ silent = false } = {}, thunkApi) => {
    try {
      const response = await chaosApi.initChaosDefaults();

      if (!silent) {
        toast(response?.data?.message || 'New Chaos actions synced', { type: 'success' });
      }

      thunkApi.dispatch(fetchChaosActions());
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to sync new Chaos actions';
      if (!silent) {
        toast(message, { type: 'error' });
      }
      return thunkApi.rejectWithValue(message);
    }
  }
);

export const updateChaosAction = createAsyncThunk(
  'chaos/update-action',
  async ({ id, payload }, thunkApi) => {
    try {
      const response = await chaosApi.updateChaosAction(id, payload);
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to update Chaos action';
      toast(message, { type: 'error' });
      return thunkApi.rejectWithValue(message);
    }
  }
);

export const testChaosAction = createAsyncThunk('chaos/test-action', async ({ id }, thunkApi) => {
  try {
    const response = await chaosApi.testChaosAction(id);
    toast(response?.data?.message || 'Chaos test command queued', { type: 'success' });
    thunkApi.dispatch(fetchChaosLogs());
    return response.data;
  } catch (error) {
    const message = error?.response?.data?.message || 'Failed to test Chaos action';
    toast(message, { type: 'error' });
    return thunkApi.rejectWithValue(message);
  }
});

export const createChaosTwitchReward = createAsyncThunk(
  'chaos/create-twitch-reward',
  async ({ id }, thunkApi) => {
    try {
      const response = await chaosApi.createTwitchReward(id);
      toast(response?.data?.message || 'Twitch reward created', { type: 'success' });
      thunkApi.dispatch(fetchChaosActions());
      thunkApi.dispatch(fetchTwitchRewards());
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to create Twitch reward';
      toast(message, { type: 'error' });
      return thunkApi.rejectWithValue(message);
    }
  }
);

export const createManualChaosTwitchReward = createAsyncThunk(
  'chaos/create-manual-twitch-reward',
  async ({ title, cost, cooldownSec, isEnabled }, thunkApi) => {
    try {
      const response = await chaosApi.createManualTwitchReward({
        title,
        cost,
        cooldownSec,
        isEnabled
      });
      toast(response?.data?.message || 'Twitch reward created', { type: 'success' });
      thunkApi.dispatch(fetchTwitchRewards());
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to create Twitch reward';
      toast(message, { type: 'error' });
      return thunkApi.rejectWithValue(message);
    }
  }
);

export const bindChaosReward = createAsyncThunk(
  'chaos/bind-reward',
  async ({ id, rewardId }, thunkApi) => {
    try {
      const response = await chaosApi.bindReward(id, rewardId);
      toast(response?.data?.message || 'Reward binding updated', { type: 'success' });
      thunkApi.dispatch(fetchChaosActions());
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to bind reward';
      toast(message, { type: 'error' });
      return thunkApi.rejectWithValue(message);
    }
  }
);

export const fetchTwitchRewards = createAsyncThunk(
  'chaos/fetch-twitch-rewards',
  async (_, thunkApi) => {
    try {
      const response = await chaosApi.fetchTwitchRewards();
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to fetch Twitch rewards';
      toast(message, { type: 'error' });
      return thunkApi.rejectWithValue(message);
    }
  }
);

export const syncChaosTwitchRewards = createAsyncThunk(
  'chaos/sync-twitch-rewards',
  async (_, thunkApi) => {
    try {
      const response = await chaosApi.syncTwitchRewards();
      toast(response?.data?.message || 'Twitch rewards synced', { type: 'success' });
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to sync Twitch rewards';
      toast(message, { type: 'error' });
      return thunkApi.rejectWithValue(message);
    }
  }
);

export const ensureChaosEventSub = createAsyncThunk(
  'chaos/ensure-eventsub',
  async (_, thunkApi) => {
    try {
      const response = await chaosApi.ensureChaosEventSub();
      toast(response?.data?.message || 'Chaos EventSub ensured', { type: 'success' });
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to ensure Chaos EventSub';
      toast(message, { type: 'error' });
      return thunkApi.rejectWithValue(message);
    }
  }
);

export const fetchChaosClientStatus = createAsyncThunk(
  'chaos/fetch-client-status',
  async (_, thunkApi) => {
    try {
      const response = await chaosApi.fetchChaosClientStatus();
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to fetch client status';
      toast(message, { type: 'error' });
      return thunkApi.rejectWithValue(message);
    }
  }
);

export const generateChaosConnectToken = createAsyncThunk(
  'chaos/generate-connect-token',
  async ({ deviceName }, thunkApi) => {
    try {
      const response = await chaosApi.generateChaosConnectToken(deviceName);
      toast(response?.data?.message || 'Connect token generated', { type: 'success' });
      return response.data;
    } catch (error) {
      const message = error?.response?.data?.message || 'Failed to generate connect token';
      toast(message, { type: 'error' });
      return thunkApi.rejectWithValue(message);
    }
  }
);

export const fetchChaosLogs = createAsyncThunk('chaos/fetch-logs', async (_, thunkApi) => {
  try {
    const response = await chaosApi.fetchChaosLogs();
    return response.data;
  } catch (error) {
    const message = error?.response?.data?.message || 'Failed to fetch chaos logs';
    toast(message, { type: 'error' });
    return thunkApi.rejectWithValue(message);
  }
});
